import type { Session } from '@setplex/tria-api'
import type { Store } from 'effector'

import { WbsErrors } from '@bldsoft/cappie/api/wbs'
import { is, Logic } from '@bldsoft/cappie/rest'
import { api, SubscriberRegistrationStatus } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
  split,
} from 'effector'
import { createForm } from 'effector-forms'
import { or } from 'patronum'
import { debounce } from 'patronum/debounce'
import { model as i18n, t } from '~/core/i18n'
import { model as router } from '~/core/router'
import { model as session } from '~/entities/session'
import { AuthTab } from '~/features/change-auth-type'
import { model as signInModel } from '~/features/subscriber/sign-in'
import { lookbehind } from '~/shared/effector'
import { aye, exists, F, nay, noop, random, T } from '~/shared/helpers'
import { formatDateDuration } from '~/shared/helpers/date'
import { actualUserInput } from '~/shared/html-events/input'
import { logger } from '~/shared/logger'
import { digits, maxLength, minLength, required } from '~/shared/validators'
import { stopTimerError, updateTextError } from '../../shared/model'
import { humanizeTo, retoken, untoken } from '../../shared/tools'

const MAX_REGISTRATION_CHECKS = 15
const MAX_SIGN_IN_TRIES = 5
const REGISTRATION_CHECK_INITIAL_INTERVAL = 3000 // progression, starts from 3 second
const REGISTRATION_CHECK_INITIAL_RATIO = 2.6 // each next item = x * (r * ratio_ratio)
const REGISTRATION_CHECK_INITIAL_RATIO_RATIO = 0.87 // like 3, 10.8, 28.44, 63.17, 122.61, 211.16, 325.91, 455.28, 582.18, 690.46, 770.86, 822.78, 851.96, 866.23, 872.3 seconds
const AUTH_DELAY_INTERVAL = [2000, 3000] as const // between 2 and 3 seconds
const INCREASED_AUTH_DELAY_INTERVAL = [4000, 5000] as const // between 4 and 5 seconds

// init page
export const init = createEvent()

// request new confirmation code
export const requestNewConfirmationCode = createEvent()

// registration by email status and result
export const getRegistrationStatus = createEvent()
export const registrationFailed = createEvent()
export const registrationSucceed = createEvent()

// registration by phone status and result
export const getRegistrationStatusByPhone = createEvent()
export const registrationByPhoneFailed = createEvent()
export const registrationByPhoneSucceed = createEvent<Session>()

// sign-in request
export const signIn = createEvent<number | void>()

// sign-in result
export const signInFailed = createEvent()

// parse data from location hash
export const parsed = createEvent<{
  id: string // registration uuid to confirm code
  from: number // `id` request time
  to: number // end of life of `id`
}>()

type State = {
  email?: string
  username?: string
  password?: string
  codecnt?: number
  failcnt?: number
  phoneNumber?: string
}

// get data from location state
export const stated = createEvent<State>()

// confirm code request effect by email
export const confirmCodeRegistrationFx = attach({
  effect: api.subscriber.confirmCodeRegistrationFx,
})

// confirm code request effect by phone
export const confirmCodeRegistrationByPhoneFx = attach({
  effect: api.subscriber.confirmCodeRegistrationByPhoneFx,
})

// get registration status effect
export const getRegistrationStatusFx = attach({
  effect: api.subscriber.getRegistrationStatusFx,
})

// resend confirmation code effect by email
export const resendConfirmationEmailFx = attach({
  effect: api.subscriber.resendConfirmationEmailFx,
})

// resend confirmation code effect by phone
export const resendConfirmationPhoneFx = attach({
  effect: api.subscriber.resendConfirmationPhoneFx,
})

// sign in request effect
export const signInFx = attach({ effect: signInModel.signInFx })

// effect to create new hash token
const retokenFx = createEffect<{ id: string; to: number }, string>(
  ({ id, to }) => retoken(id, to)
)

// effect to create hash token
const untokenFx = createEffect<
  string,
  { id: string; from: number; to: number }
>((token) => untoken(token))

// effect for waiting
const signInDelayFx = createEffect<number, void>(
  (time) => new Promise((resolve) => setTimeout(resolve, time))
)
const getRegistrationStatusDelayFx = createEffect<number, void>(
  (time) => new Promise((resolve) => setTimeout(resolve, time))
)

// registration uuid to confirm code
export const $id = createStore<string | null>(null) //
  .on(parsed, (__, { id }) => id)

// registration uuid request time
export const $from = createStore<number | null>(null) //
  .on(parsed, (__, { from }) => from)

// registration uuid end of life
export const $to = createStore<number | null>(null) //
  .on(parsed, (__, { to }) => to)

// registration email
export const $email = createStore<string | null>(null) //
  .on(stated, (__, { email }) => email ?? null)
  .reset(init)

// username
export const $username = createStore<string | null>(null) //
  .on(stated, (__, { username }) => username ?? null)

// password
export const $password = createStore<string | null>(null) //
  .on(stated, (__, { password }) => password ?? null)

// registration phone
export const $phoneNumber = createStore<string | null>(null)
  .on(stated, (__, { phoneNumber }) => phoneNumber ?? null)
  .reset(init)

// counter of requested codes
export const $codeCounter = createStore<number>(0) //
  .reset(init)
  .on(stated, (__, { codecnt }) => codecnt ?? 0)

// counter of failed code enters
export const $failedCounter = createStore<number>(0) //
  .reset(init)
  .on(stated, (__, { failcnt }) => failcnt ?? 0)
  .on(api.events.logic.confirmationCodeInvalid, (x) => x + 1)

// delay before each registration check request
// changes in variadic geometric progression
// with initial = 1, initial ratio = 2 and ratio_ratio = 0.936
// delays for 15 steps will be
// 1, 2, 3.74, 6.56, 10.75, 16.51, 23.73, 31.91, 40.17, 47.33, 52.20, 53.88, 52.06, 47.08, 39.85 seconds
// which in sum ~ 7.14 minutes
export const $statusChecksDelay = createStore<[number, number]>([
  REGISTRATION_CHECK_INITIAL_INTERVAL,
  REGISTRATION_CHECK_INITIAL_RATIO,
])
  .reset(init)
  .on(getRegistrationStatusFx, ([delay, ratio]) => [
    delay * ratio,
    ratio * REGISTRATION_CHECK_INITIAL_RATIO_RATIO,
  ])

// count of registration check requests
export const $statusChecksCount = createStore<number>(0) //
  .reset(init)
  .on(getRegistrationStatusFx, (x) => x + 1)

// count of sign-in requests
export const $signInTriesCount = createStore<number>(0) //
  .reset(init)
  .on(signInFx, (x) => x + 1)

// error to show to the user
export const $error = createStore<string | null>(null)

// debounced error, in order to prevent react to render component with old data
export const $viewError = createStore<string | null>(null)

export const $lockedError = createStore<boolean>(false)
  .reset(init, signInFx.fail)
  .on(confirmCodeRegistrationFx.done, T)

// Transformed error state into a displayable message with translation
export const $message = combine(
  $viewError,
  $lockedError,
  i18n.$t,
  (error, lockedError, { t }) => {
    const show = Boolean(error) && !lockedError
    return { show, title: show ? t(error!) : undefined }
  }
)

// permanent error, blocking registration process
export const $permError = createStore<string | null>(null)

// lock interface after successful registration
export const $locked = createStore<boolean>(false)
  .reset(init)
  .on(confirmCodeRegistrationFx.done, T)

export const $pendingRegistrationByPhone = createStore<boolean>(false)
  .on(getRegistrationStatusByPhone, T)
  .on(confirmCodeRegistrationByPhoneFx.pending, T)
  .on(registrationByPhoneFailed, F)
  .on(registrationByPhoneSucceed, F)
  .reset(init)

export const $pendingRegistration = or(
  confirmCodeRegistrationFx.pending,
  session.getAccountFx.pending,
  $pendingRegistrationByPhone
)

export const $translationEmail = combine(
  i18n.$t,
  $email,
  ({ t }, email) => email ?? t('code_sent_requested_email_web')
)

export const $translationPhoneNumber = combine(
  i18n.$t,
  $phoneNumber,
  ({ t }, email) => email ?? t('requested_phone_web')
)

export const $verificationTypeTranslation: Store<string> = combine(
  router.$search,
  $translationEmail,
  $translationPhoneNumber,
  (payload, translationEmail, translationPhoneNumber) =>
    payload?.replace('?=', '') === AuthTab.Phone
      ? translationPhoneNumber
      : translationEmail
)

// confirm code form
export const confirmForm = createForm({
  fields: {
    code: {
      init: '' as string,
      rules: [
        required(),
        digits(),
        maxLength.validator(6),
        minLength.validator(6),
      ],
    },
  },
  validateOn: ['submit'],
})

// last verification code need for check status creating subscription
export const $lastCode = createStore<string | null>(null)
  .on(confirmForm.formValidated, (__, { code }) => code)
  .reset(registrationByPhoneSucceed)

const userUpdatesForm = actualUserInput(confirmForm.$values.updates)

// add some magic -> autovalidate and submit form when user types 6 chars
sample({
  clock: confirmForm.fields.code.$value,
  filter: (code) => code?.length >= 6,
  target: confirmForm.validate,
})

// send verify code request
sample({
  clock: confirmForm.formValidated,
  source: { id: $id, email: $email },
  filter: ({ id, email }) => Boolean(id) && Boolean(email),

  fn: ({ id }, { code }) => ({ id: id!, code }),
  target: confirmCodeRegistrationFx,
})

// send verify code request
sample({
  clock: confirmForm.formValidated,
  source: {
    id: $id,
    phoneNumber: $phoneNumber,
    rememberMe: signInModel.$rememberMe,
  },
  filter: ({ id, phoneNumber }) => Boolean(id) && Boolean(phoneNumber),

  fn: ({ id, rememberMe }, { code }) => ({ id: id!, code, rememberMe }),
  target: confirmCodeRegistrationByPhoneFx,
})

// reset form on init route
sample({
  clock: init,
  target: confirmForm.reset,
})

// decode hash token to get `id` and `id`s end of life
sample({
  clock: init,
  source: router.$hash,
  filter: Boolean,
  fn: (hash) => hash.replace(/^#?/, ''),
  target: untokenFx,
})

// in case there is no hash -> redirect back to /sign-up
sample({
  clock: init,
  source: router.$hash,
  filter: (hash) => nay(exists(hash)),
  fn: () => ({ pathname: '/sign-up' }),
  target: router.navigateReplace,
})

// in case of failed to decode hash -> redirect back to /sign-up
sample({
  clock: untokenFx.fail,
  fn: () => ({ pathname: '/sign-up' }),
  target: router.navigateReplace,
})

// in case we could not decode token -> log error
sample({
  clock: untokenFx.fail,
  fn: (fail) => ['could not decode token:', fail],
  target: logger.errorFx,
})

// get `id` and `id`s end of life
sample({
  clock: untokenFx.doneData,
  target: parsed,
})

// get `email`, `username` and `password` from history state
// this will get preserved in browser, but if user shared his verify link -> state would be empty
sample({
  clock: init,
  source: router.$state as Store<State | null>,
  filter: Boolean,
  target: stated,
})

// request new confirmation code
sample({
  clock: requestNewConfirmationCode,
  source: { id: $id, email: $email },
  filter: ({ id, email }) => Boolean(id) && Boolean(email),

  fn: ({ id }) => ({ id: id! }),
  target: resendConfirmationEmailFx,
})

// request new confirmation code
sample({
  clock: requestNewConfirmationCode,
  source: { id: $id, phoneNumber: $phoneNumber },
  filter: ({ id, phoneNumber }) => Boolean(id) && Boolean(phoneNumber),

  fn: ({ id }) => ({ id: id! }),
  target: resendConfirmationPhoneFx,
})

// regenerate token to shift start time
sample({
  clock: [resendConfirmationEmailFx, resendConfirmationPhoneFx],
  source: { id: $id, to: $to },
  filter: ({ id, to }) => exists(id) && exists(to),
  fn: ({ id, to }) => ({ id: id!, to: to! }),
  target: retokenFx,
})

// in case we could not encode token -> log error
sample({
  clock: retokenFx.fail,
  fn: (fail) => ['could not encode new token:', fail],
  target: logger.errorFx,
})

// redirect to verify page (to replace token)
sample({
  clock: retokenFx.doneData,
  source: {
    location: router.$location,
    codecnt: $codeCounter,
    failcnt: $failedCounter,
  },
  filter: ({ location }) => exists(location),
  fn: ({ location, codecnt, failcnt }, token) => ({
    ...location,
    hash: token,
    state: {
      ...(location!.state as State),
      codecnt: codecnt + 1,
      failcnt,
    },
  }),
  target: router.navigateReplace,
})

// just reset code field on failed confirmation
sample({
  clock: [
    confirmCodeRegistrationFx.fail,
    confirmCodeRegistrationByPhoneFx.fail,
  ],
  fn: () => '',
  target: confirmForm.fields.code.$value,
})

// check registration status on successful email confirmation
sample({
  clock: confirmCodeRegistrationFx.done,
  target: getRegistrationStatus,
})

//
// check registration status on successful email confirmation
//

// less than `MAX_REGISTRATION_CHECKS` tries -> try to get status
sample({
  clock: [getRegistrationStatus, getRegistrationStatusByPhone],
  source: { count: $statusChecksCount, delay: $statusChecksDelay },
  filter: ({ count }) => count < MAX_REGISTRATION_CHECKS,
  fn: ({ delay }) => delay[0],
  target: getRegistrationStatusDelayFx,
})

// more than `MAX_REGISTRATION_CHECKS` tries -> show error message
sample({
  clock: getRegistrationStatus,
  source: { count: $statusChecksCount },
  filter: ({ count }) => count >= MAX_REGISTRATION_CHECKS,
  target: registrationFailed,
})

// request checking status registration
sample({
  clock: getRegistrationStatusDelayFx.done,
  source: { id: $id, email: $email },
  filter: ({ id, email }) => Boolean(id) && Boolean(email),

  fn: ({ id }) => ({ id: id! }),
  target: getRegistrationStatusFx,
})

// got response from registration status check
split({
  source: getRegistrationStatusFx.doneData,
  match: ({ status }): 'done' | 'fail' | 'retry' => {
    switch (status) {
      case SubscriberRegistrationStatus.DONE:
        return 'done'
      case SubscriberRegistrationStatus.IN_PROCESS:
      case SubscriberRegistrationStatus.WAITING_EMAIL_CONFIRMATION:
        return 'retry'
      case SubscriberRegistrationStatus.FAILED:
      case SubscriberRegistrationStatus.EXPIRED:
      default:
        return 'fail'
    }
  },
  cases: {
    done: registrationSucceed,
    fail: registrationFailed,
    retry: getRegistrationStatus, // recheck status after timeout
  },
})

// if registration status check failed
sample({
  clock: getRegistrationStatusFx.fail,
  target: getRegistrationStatus, // retry status check after timeout, just in case
})

//
// check registration status on successful phone confirmation
//

sample({
  clock: confirmCodeRegistrationByPhoneFx.finally,
  filter: ({ status }) => status === 'fail',
  target: registrationByPhoneFailed,
})

sample({
  clock: confirmCodeRegistrationByPhoneFx.doneData,
  filter: ({ code }) => code === 202,
  target: getRegistrationStatusByPhone,
})

// succeed registration by phone
sample({
  clock: confirmCodeRegistrationByPhoneFx.doneData,
  filter: ({ code }) => code === 200,
  fn: ({ payload }) => payload,
  target: registrationByPhoneSucceed,
})

sample({
  clock: registrationByPhoneSucceed,
  filter: ({ toa }) => toa == null,
  fn: noop,
  target: session.getAccountFx,
})
sample({
  clock: registrationByPhoneSucceed,
  filter: ({ toa }) => toa != null,
  fn: () => '/toa',
  target: router.navigateReplace,
})

// redirect on home page after succeed registration by phone number
sample({
  clock: session.getAccountFx.doneData,
  source: confirmCodeRegistrationByPhoneFx.doneData,
  filter: ({ code }) => code === 200,
  fn: () => '/',
  target: router.navigateReplace,
})

// request checking status registration
sample({
  clock: getRegistrationStatusDelayFx.done,
  source: {
    id: $id,
    phone: $phoneNumber,
    code: $lastCode,
    rememberMe: signInModel.$rememberMe,
    prev: router.$previous,
  },
  filter: ({ id, phone }) => Boolean(id) && Boolean(phone),
  fn: (data) => {
    const { id, code, rememberMe, prev } = data as {
      id: string
      code: string
      rememberMe: boolean
      prev: Location | null
    }

    return prev?.pathname.startsWith('/sign-in')
      ? {
          id,
          code,
          rememberMe,
        }
      : { id, code, rememberMe: true }
  },
  target: confirmCodeRegistrationByPhoneFx,
})

//
// try to autologin after successful email confirmation
//

// in case of successful confirmation, if we lost email or password — just show success message
sample({
  clock: registrationSucceed,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => nay(email) || nay(password),
  fn: () => ({ pathname: '/sign-up/done' }),
  target: router.navigatePush,
})

// in case of successful confirmation, if we HAVE email and password — wait a bit and try to login
sample({
  clock: registrationSucceed,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => aye(email) && aye(password),
  fn: () => 0, // no delay after successful registration
  target: signIn,
})

// less than `MAX_SIGN_IN_TRIES` tries -> try to get status
sample({
  clock: signIn,
  source: $signInTriesCount,
  filter: (count) => count < MAX_SIGN_IN_TRIES,
  fn: (_, timeout) => timeout ?? random(...AUTH_DELAY_INTERVAL),
  target: signInDelayFx,
})

// more than `MAX_SIGN_IN_TRIES` tries -> redirect to /sign-up/done
// because registration status was DONE, so, subscriber is created,
// but we cannot authenticate by some reason
sample({
  clock: signIn,
  source: $signInTriesCount,
  filter: (count) => count >= MAX_SIGN_IN_TRIES,
  target: signInFailed,
})

// lost email or password after timeout??? WAT? show success message
sample({
  clock: signInDelayFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => nay(email) || nay(password),
  fn: () => ({ pathname: '/sign-up/done' }),
  target: router.navigatePush,
})

// cannot authenticate by some unknown reason,
// but subscriber was created nonetheless
sample({
  clock: signInFailed,
  fn: () => ({ pathname: '/sign-up/done' }),
  target: router.navigatePush,
})

// still has email and password -> try to autologin
sample({
  clock: signInDelayFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => aye(email) && aye(password),
  fn: (data) => {
    const { email: username, password } = data as {
      email: string
      password: string
    }

    return {
      username,
      password,
      rememberMe: true,
    }
  },
  target: signInFx,
})

// successfully authorized -> go to Home
sample({
  clock: signInFx.done,
  fn: () => ({ pathname: '/' }),
  target: router.navigatePush,
})

// failed to authorize -> retry shortly
sample({
  clock: signInFx.failData,
  filter: (data) =>
    is(data, Logic) && !is(data, WbsErrors.deviceTypeNotAllowed),
  fn: (data) => {
    if (is(data, WbsErrors.yourIPAddressLocked)) {
      const payload = data.payload
      const remainsLockSeconds =
        payload != null &&
        typeof payload === 'object' &&
        'remainsLockSeconds' in payload &&
        typeof payload.remainsLockSeconds === 'number'
          ? payload.remainsLockSeconds
          : undefined
      return remainsLockSeconds
        ? remainsLockSeconds * 1000
        : random(...INCREASED_AUTH_DELAY_INTERVAL)
    }
  },
  target: signIn,
})

sample({
  clock: registrationFailed,
  fn: () => ({
    pathname: '/sign-up/fail',
    state: 'something_went_wrong_error',
  }),
  target: router.navigateReplace,
})

// TODO: add timer to limit auth tries, maybe 30 seconds?

//
// Permanent errors -> redirect to /fail page
//

sample({
  clock: [
    api.events.logic.tooManyCodeRequests,
    api.events.logic.noMoreCodeRequestsAllowed,
    api.events.logic.noMoreConfirmationAttemptsAllowed,
  ],
  fn: () => ({
    pathname: '/sign-up/fail',
    state: 'confirm_fail_attempt_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: api.events.logic.confirmationLinkExpired,
  fn: () => ({
    pathname: '/sign-up/fail',
    state: 'request_expired_error', // WG0038
  }),
  target: router.navigateReplace,
})

sample({
  clock: parsed,
  filter: ({ to }) => humanizeTo(to) === null,
  fn: () => ({
    pathname: '/sign-up/fail',
    state: 'request_expired_error', // WG0038
  }),
  target: router.navigateReplace,
})

sample({
  clock: [
    api.events.logic.confirmationLinkNotExist,
    api.events.logic.confirmationLinkSettingsNotFound,
  ],
  fn: () => ({
    pathname: '/sign-up/fail',
    state: 'something_went_wrong_error',
  }),
  target: router.navigateReplace,
})

//
// add listeners for server errors
//

$permError
  .reset(init)
  .on(parsed, (__, { to }) =>
    humanizeTo(to) === null ? 'request_expired_error' : null
  )
  .on(
    api.events.logic.emailAlreadyConfirmed, // WG0037 - Email address has been already confirmed
    () => 'email_already_confirmed_error'
  )
  .on(
    api.events.logic.confirmationLinkExpired, // WG0038 - The registration request has expired
    () => 'request_expired_error'
  )
  .on(
    api.events.logic.noMoreConfirmationAttemptsAllowed, // WG0064 - No more attempts are allowed (too many attempts)
    () => 'confirm_fail_attempt_error'
  )
  .on(
    api.events.logic.noMoreCodeRequestsAllowed, // WG0048 | WG0067
    () => 'code_request_attempt_error'
  )

// need to hide http errors on auth requests, because they are *expected*
const signInHttpError = lookbehind({
  source: signInFx.fail,
  after: api.events.http.error,
  within: 100,
})

$error
  .reset(
    userUpdatesForm,
    signInHttpError,
    init,

    // "server validation" errors
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.subscriptionHasBeenExpired, // WG0009 - Subscription has been expired
    () => 'subscription_expired_error'
  )
  .on(
    api.events.logic.accountOrDeviceHasBeenSuspended, // WG0015
    () => 'account_or_device_suspended_error'
  )
  .on(
    api.events.logic.emailAlreadyConfirmed, // WG0037 - Email address has been already confirmed
    () => 'email_already_confirmed_error'
  )
  .on(
    api.events.logic.confirmationLinkExpired, // WG0038 - The registration request has expired
    () => 'request_expired_error'
  )
  .on(
    api.events.logic.confirmationCodeInvalid, // WG0063 - The confirmation code is invalid
    () => 'invalid_sms_code_error'
  )
  .on(
    api.events.logic.noMoreConfirmationAttemptsAllowed, // WG0064 - No more attempts are allowed (too many attempts)
    () => 'confirm_fail_attempt_error'
  )
  .on(
    api.events.logic.noMoreCodeRequestsAllowed, // WG0048 | WG0067
    () => 'code_request_attempt_error'
  )
  .on(
    api.events.logic.tooManyCodeRequests, // WG0065 | WG0070 | WG0095
    () => 'code_request_attempt_error'
  )
  .on(
    api.events.logic.confirmationLinkNotExist, // WG0039
    () => 'confirmation_link_not_exist_error'
  )
  .on(
    api.events.logic.confirmationLinkSettingsNotFound, // WG0077
    () => 'confirmation_link_not_exist_error'
  )
  .on(
    api.events.logic.deviceTypeNotAllowed, // EC21 | WG0016
    () => 'web_app_disabled_error'
  )
  .on(
    [
      api.events.logic.internalServerError, // 500 | WB0500 | WG0500
      api.events.logic.unexpectedError, // WG9999
    ],
    () => 'unknown_error'
  )
  .on(
    api.events.logic.sessionNotExist, // WG0098
    () => 'session_not_exist_error'
  )
  // TODO: It is necessary to reconsider the approach to the implementation of error translation with additional options, also need to add dynamic language update
  .on(
    api.events.logic.noMoreVerifyAttempts, // WG0099
    (_, { payload }) => {
      const time = formatDateDuration({
        duration: {
          seconds: payload?.remainsSeconds,
        },
      })
      return t('invalid_sms_code_attempt_with_time_error', {
        time,
      })
    }
  )
  .on(
    api.events.logic.codeIsNotValid, // WG0100
    () => 'invalid_sms_code_error'
  )

  // some generic request errors
  .on(
    api.events.http.httpError, // any non-200 error
    (error) => (error === null ? 'unknown_error' : error)
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: $viewError,
})

sample({
  clock: updateTextError,
  fn: (data) => data.translated,
  target: $viewError,
})

sample({
  clock: [userUpdatesForm, init],
  target: stopTimerError,
})

$viewError.reset(userUpdatesForm, init)
